import { encodePlayer } from "@dust/world/internal";
import {
  camelToSpaces,
  decodeTransactionError,
  formatEther,
  getObjectImageUrl,
  objectTypes,
  transferSystemId,
} from "@permutation-city/common";
import {
  ActionButton,
  BarLoader,
  Divider,
  Input,
  useHotkeys,
} from "@permutation-city/ui";
import { useMutation } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import { ArrowRightIcon } from "lucide-react";
import { useCallback, useState } from "react";
import type { Hex } from "viem";
import { useDustClient } from "../../hooks/useDustClient";
import { useIncentivesQuery } from "../../queries/useIncentivesQuery";
import { abi } from "../abi";
import { useBatteriesAmountQuery } from "./useBatteriesAmountQuery";

type Props = {
  title?: string;
  subtitle?: React.ReactNode;
  description?: string;
  objectId?: number;
};

// TODO: clean up
const energize = {
  title: "Energize the force field",
  subtitle: (
    <>
      Deposit <ArrowRightIcon className="inline-block w-4 h-4 -mt-1" /> energize
      the force field <ArrowRightIcon className="inline-block w-4 h-4 -mt-1" />{" "}
      earn $P
    </>
  ),
  description:
    "Permutation Pesos ($P) is the labor-backed currency of Permutation City. It is rewarded for contributions to the city in the form of energy or labor. It can be used to acquire a passport and to participate in trade within the city.",
  objectId: 32793,
};

export function DepositForm({ objectId: defaultObjectId }: Props) {
  const params = useParams({ strict: false });
  const { data: dustClient } = useDustClient();
  const userAddress = dustClient?.appContext.userAddress;
  const chestEntityId = dustClient?.appContext.via?.entity;
  const userEntityId = userAddress ? encodePlayer(userAddress) : undefined;
  const [amount, setAmount] = useState("1");
  const { data: maxBatteriesAmount } = useBatteriesAmountQuery(userEntityId);
  const { data: incentives, isLoading } = useIncentivesQuery();

  const incentive = incentives?.find(
    (incentive) =>
      incentive.objectId === defaultObjectId || Number(params.objectId),
  );

  const itemId = incentive?.objectId;
  const item = objectTypes[itemId as keyof typeof objectTypes];

  const depositMutation = useMutation({
    mutationFn: async () => {
      if (!dustClient) {
        throw new Error("Could not connect to Dust client");
      }
      if (!userAddress) {
        throw new Error("User not found");
      }

      const userSlots = await dustClient.provider.request({
        method: "getSlots",
        params: {
          entity: userEntityId as Hex,
          objectType: Number(itemId),
          amount: Number(amount),
          operationType: "withdraw",
        },
      });

      const result = await dustClient.provider.request({
        method: "systemCall",
        params: [
          {
            systemId: transferSystemId,
            abi,
            functionName: "transferAmounts",
            args: [
              userEntityId,
              userEntityId,
              chestEntityId,
              userSlots.slots,
              "0x",
            ],
          },
        ],
      });

      const errorMessage = decodeTransactionError(abi, result);
      if (errorMessage) {
        throw new Error(errorMessage);
      }
      return result;
    },
  });

  const handleDeposit = useCallback(() => {
    depositMutation.mutate();
  }, [depositMutation]);

  useHotkeys("ctrl + D", () => handleDeposit());
  useHotkeys("ctrl + M", () => setAmount(String(maxBatteriesAmount)));

  if (isLoading) {
    return <BarLoader intervalRate={100} />;
  }

  if (!incentive) {
    return (
      <div>
        <p className="uppercase">No incentive found :(</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center w-full max-w-full sm:max-w-sm mx-auto space-y-6">
      {itemId === energize.objectId && (
        <div className="flex flex-col gap-2 w-full text-center">
          <h1 className="uppercase">{energize.title}</h1>
          <p className="text-white/70 uppercase">{energize.subtitle}</p>
        </div>
      )}

      <div className="flex flex-col gap-4 justify-start w-full text-center">
        <h1 className="uppercase">{camelToSpaces(item?.name)}</h1>
        <img
          src={getObjectImageUrl(itemId ?? 0)}
          alt={item?.name}
          className="w-[90px] h-[90px] bg-[#447AE2] p-2 border-4 border-solid border-white mx-auto"
        />
      </div>

      <form className="w-full space-y-6 mt-4">
        <Input
          label="AMOUNT"
          type="number"
          value={amount}
          onChange={(event) => setAmount(event.target.value)}
        />

        <div className="uppercase">
          <div className="flex justify-between">
            <span className="text-gray-600 opacity-70">Reward per item:</span>
            <span className="font-medium">
              $P {formatEther(incentive.tokenAmount)}
            </span>
          </div>

          <div>
            <div className="flex justify-between">
              <span className="font-medium opacity-70">Total reward:</span>
              <span className="font-medium">
                $P{" "}
                {incentive
                  ? formatEther(incentive.tokenAmount * BigInt(amount))
                  : 0}
              </span>
            </div>
          </div>
        </div>

        <div className="flex gap-2 justify-between">
          <ActionButton
            hotkey="^+M"
            onClick={() => setAmount(String(maxBatteriesAmount))}
            disabled={!maxBatteriesAmount || maxBatteriesAmount === 0n}
          >
            Max
          </ActionButton>

          <ActionButton
            hotkey="^+D"
            onClick={handleDeposit}
            disabled={depositMutation.isPending}
          >
            Deposit
          </ActionButton>
        </div>

        {depositMutation.isPending && <BarLoader intervalRate={100} />}
        {depositMutation.isSuccess && (
          <p className="uppercase text-success text-right">Success!</p>
        )}
        {depositMutation.isError && (
          <p className="uppercase text-danger text-right">
            {depositMutation.error?.message || "Something went wrong"}
          </p>
        )}
      </form>

      {itemId === energize.objectId && (
        <>
          <Divider />
          <p className="text-left text-white/70 uppercase">
            {energize.description}
          </p>
        </>
      )}
    </div>
  );
}
